import React from 'react'
import cn from 'classnames'
import DatoImage from '../../../components/DatoRichText/DatoImage'
import { Link } from 'react-router-dom'
import Arrow from '../assets/arrow-right.svg?svgr-webpack'
import { TeaserFragment as TeaserFragmentProps } from './types/TeaserFragment'
import { captureException } from 'js/utils/captureException'

type ImageRelatedTeaserFragmentProps = Pick<
  TeaserFragmentProps,
  'rectangularImage' | 'squareImage' | 'image'
>

type ExcerptProps = {
  content: string
}

function Excerpt({ content }: ExcerptProps) {
  const wordsArray = content.split(' ')
  const last = wordsArray.pop()

  return (
    <>
      {wordsArray.join(' ')}
      <span className="whitespace-nowrap" key="last">
        {` ${last}`}
        <span
          className={`
              h-24 w-24
              inline-flex ml-6
              transition transform origin-left lg:scale-125
              group-hover:translate-x-1
              text-green group-hover:text-white
              bg-transparent group-hover:bg-green rounded-full
              group-hover:shadow-elevated
            `}
        >
          <Arrow className="m-auto" />
        </span>
      </span>
    </>
  )
}

type ImageProps = {
  big: boolean
  className: string
  slug: string // just used for creating errors if image is missing
} & ImageRelatedTeaserFragmentProps
const Image = ({
  image,
  big,
  rectangularImage,
  squareImage,
  className,
  slug,
}: ImageProps) => {
  const selectedImage = big ? rectangularImage : squareImage
  const imageUrlParams = new URLSearchParams(selectedImage?.url)
  if (!image || !rectangularImage || !squareImage) {
    captureException(
      `missing image on nutrition article with slug: ${slug} on (${
        window && window.location?.href
      })`
    )
    return null
  }

  return (
    <DatoImage
      width={imageUrlParams.get('w') || undefined}
      height={imageUrlParams.get('h') || undefined}
      alt={image.alt}
      title={image.title || undefined}
      src={selectedImage?.url as string}
      lazy
      base64={image.blurUpThumb}
      className={className}
    />
  )
}

type Props = {
  big?: boolean
  slug: string
  title?: string
  excerpt: string
} & ImageRelatedTeaserFragmentProps

const Teaser = ({ big = false, excerpt, slug, title, ...rest }: Props) => {
  return (
    <div className={cn('group flex relative text-left', big && 'flex-col')}>
      <div className={cn(!big && 'shrink-0 w-1/4 lg:w-1/3 xxl:w-1/4')}>
        <Image
          slug={slug}
          big={big}
          {...rest}
          className="rounded-8 lg:rounded-12 xl:rounded-16 w-full"
        />
      </div>
      <div
        className={
          big
            ? undefined
            : `
          my-auto ml-16 md:ml-24 xl:ml-32
          md:w-3/4 lg:w-2/3 xxl:w-3/4
        `
        }
      >
        <Link to={`/nutrition-explained/${slug}`} className="fill-link">
          <h3
            className={cn(
              'mb-6 lg:mb-12 leading-default type-med-minus-fat text-Type',
              big && 'mt-20'
            )}
          >
            <span
              className={cn(
                'transition',
                'border-b-3 xl:border-b-4 border-transparent',
                'group-hover:border-green'
              )}
            >
              {title}
            </span>
          </h3>
        </Link>
        <p className="leading-body">
          <Excerpt content={excerpt as string} />
        </p>
      </div>
    </div>
  )
}

export const TeaserFragment = gql`
  fragment TeaserFragment on NutritionArticleRecord {
    id
    slug
    title
    excerpt
    image {
      alt
      title
      blurUpThumb
    }
    squareImage: image {
      url(imgixParams: { w: 320, h: 320, fm: jpg, fit: crop, crop: focalpoint })
    }
    rectangularImage: image {
      url(imgixParams: { w: 480, h: 320, fm: jpg, fit: crop, crop: focalpoint })
    }
  }
`

export default Teaser
